import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Wrench, CreditCard, Settings2, Plus, Trash2, Ban, CheckCircle2, RotateCcw, Unlock, Power, FileCog, Send, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const TRIGGER_MSGS = ["StatusNotification", "BootNotification", "Heartbeat", "MeterValues", "DiagnosticsStatusNotification", "FirmwareStatusNotification"];

function Section({ title, children }) {
  return (
    <div className="space-y-2 rounded-lg border p-3">
      <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground">{title}</div>
      {children}
    </div>
  );
}

// ---------------- Manutenzione ----------------
function MaintTab({ sid }) {
  const [faults, setFaults] = useState(null);
  const [busy, setBusy] = useState(false);
  const [resetType, setResetType] = useState("Soft");
  const [conn, setConn] = useState(1);
  const [avail, setAvail] = useState("Operative");
  const [trigMsg, setTrigMsg] = useState("StatusNotification");
  const [cfgKey, setCfgKey] = useState("");
  const [cfgVal, setCfgVal] = useState("");

  const loadFaults = async () => {
    try { const r = await api.get(`/ev/stations/${sid}/faults`); setFaults(r.data?.faults || []); } catch { setFaults([]); }
  };
  useEffect(() => { loadFaults(); /* eslint-disable-next-line */ }, []);

  const send = async (payload, msg) => {
    setBusy(true);
    try { await api.post(`/ev/stations/${sid}/maintenance`, payload); toast.success(msg); loadFaults(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Comando non riuscito"); }
    finally { setBusy(false); }
  };
  const getCfg = async () => {
    setBusy(true);
    try { await api.post(`/ev/stations/${sid}/get-configuration`); toast.success("GetConfiguration inviato"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Comando non riuscito"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="ev-maint-tab">
      <Section title="Guasti e anomalie ricevuti">
        {faults == null ? <p className="text-sm text-muted-foreground">Caricamento…</p>
          : faults.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="ev-maint-nofaults">Nessun guasto segnalato.</p>
            : <div className="space-y-1" data-testid="ev-maint-faults">
              {faults.map((f, i) => (
                <div key={i} className="flex items-start gap-2 rounded-md bg-red-50 px-2 py-1.5 text-xs text-red-700">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span>{f.code || f.error_code || "—"}{f.info ? ` · ${f.info}` : ""}{f.at ? ` · ${new Date(f.at).toLocaleString("it-IT")}` : ""}</span>
                </div>
              ))}
            </div>}
      </Section>

      <Section title="Riavvio (Reset)">
        <div className="flex flex-wrap items-center gap-2">
          <select value={resetType} onChange={(e) => setResetType(e.target.value)} className="h-9 rounded-md border px-2 text-sm" data-testid="ev-maint-reset-type">
            <option value="Soft">Soft</option><option value="Hard">Hard</option>
          </select>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => send({ action: "Reset", type: resetType }, "Reset inviato")} data-testid="ev-maint-reset-btn"><RotateCcw className="mr-1 h-4 w-4" />Riavvia</Button>
        </div>
      </Section>

      <Section title="Connettore">
        <div className="flex flex-wrap items-center gap-2">
          <Label className="text-sm">N°</Label>
          <Input type="number" value={conn} onChange={(e) => setConn(Number(e.target.value) || 1)} className="h-9 w-16" data-testid="ev-maint-conn" />
          <Button size="sm" variant="outline" disabled={busy} onClick={() => send({ action: "UnlockConnector", connector_id: conn }, "Sblocco inviato")} data-testid="ev-maint-unlock-btn"><Unlock className="mr-1 h-4 w-4" />Sblocca</Button>
          <select value={avail} onChange={(e) => setAvail(e.target.value)} className="h-9 rounded-md border px-2 text-sm" data-testid="ev-maint-avail-type">
            <option value="Operative">Operativa</option><option value="Inoperative">Fuori servizio</option>
          </select>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => send({ action: "ChangeAvailability", type: avail, connector_id: conn }, "Disponibilità aggiornata")} data-testid="ev-maint-avail-btn"><Power className="mr-1 h-4 w-4" />Applica</Button>
        </div>
      </Section>

      <Section title="Configurazione (avanzato)">
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="outline" disabled={busy} onClick={getCfg} data-testid="ev-maint-getconfig-btn"><FileCog className="mr-1 h-4 w-4" />Leggi configurazione</Button>
          <select value={trigMsg} onChange={(e) => setTrigMsg(e.target.value)} className="h-9 rounded-md border px-2 text-sm" data-testid="ev-maint-trigger-msg">
            {TRIGGER_MSGS.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => send({ action: "TriggerMessage", requested_message: trigMsg, connector_id: conn }, "TriggerMessage inviato")} data-testid="ev-maint-trigger-btn"><Send className="mr-1 h-4 w-4" />Richiedi</Button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Input placeholder="Chiave" value={cfgKey} onChange={(e) => setCfgKey(e.target.value)} className="h-9 w-40" data-testid="ev-maint-cfg-key" />
          <Input placeholder="Valore" value={cfgVal} onChange={(e) => setCfgVal(e.target.value)} className="h-9 w-40" data-testid="ev-maint-cfg-val" />
          <Button size="sm" variant="outline" disabled={busy || !cfgKey} onClick={() => send({ action: "ChangeConfiguration", key: cfgKey, value: cfgVal }, "ChangeConfiguration inviato")} data-testid="ev-maint-cfg-btn">Imposta</Button>
        </div>
      </Section>
    </div>
  );
}

// ---------------- Tessere RFID ----------------
function TagsTab({ sid }) {
  const [tags, setTags] = useState(null);
  const [idTag, setIdTag] = useState("");
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => { try { const r = await api.get(`/ev/stations/${sid}/tags`); setTags(r.data?.tags || []); } catch { setTags([]); } };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const add = async () => {
    if (!idTag.trim()) return;
    setBusy(true);
    try { await api.post(`/ev/stations/${sid}/tags`, { id_tag: idTag.trim(), label: label.trim() || undefined }); toast.success("Tessera aggiunta"); setIdTag(""); setLabel(""); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Aggiunta non riuscita"); }
    finally { setBusy(false); }
  };
  const toggle = async (t) => {
    try { await api.patch(`/ev/stations/${sid}/tags/${t.id}`, { blocked: !t.blocked }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Operazione non riuscita"); }
  };
  const del = async (t) => {
    try { await api.delete(`/ev/stations/${sid}/tags/${t.id}`); toast.success("Tessera rimossa"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Rimozione non riuscita"); }
  };
  const sync = async () => {
    setBusy(true);
    try { await api.post(`/ev/stations/${sid}/sync-local-list`); toast.success("Lista tessere inviata alla stazione"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Invio non riuscito"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="ev-tags-tab">
      <Section title="Aggiungi tessera">
        <div className="flex flex-wrap items-center gap-2">
          <Input placeholder="ID tessera (idTag)" value={idTag} onChange={(e) => setIdTag(e.target.value)} className="h-9 w-48" data-testid="ev-tag-idtag" />
          <Input placeholder="Etichetta (facoltativa)" value={label} onChange={(e) => setLabel(e.target.value)} className="h-9 w-56" data-testid="ev-tag-label" />
          <Button size="sm" disabled={busy || !idTag.trim()} onClick={add} data-testid="ev-tag-add-btn"><Plus className="mr-1 h-4 w-4" />Aggiungi</Button>
        </div>
      </Section>
      <div className="space-y-1" data-testid="ev-tags-list">
        {tags == null ? <p className="text-sm text-muted-foreground">Caricamento…</p>
          : tags.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="ev-tags-empty">Nessuna tessera.</p>
            : tags.map((t) => (
              <div key={t.id} className="flex items-center gap-2 rounded-lg border px-3 py-2 text-sm" data-testid={`ev-tag-${t.id}`}>
                <CreditCard className="h-4 w-4 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium">{t.label || t.id_tag}</div>
                  <div className="truncate font-mono text-xs text-muted-foreground">{t.id_tag}{t.system ? " · di sistema" : ""}</div>
                </div>
                {t.blocked && <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">Bloccata</span>}
                {!t.system && (
                  <>
                    <Button size="icon" variant="ghost" className="h-8 w-8" title={t.blocked ? "Sblocca" : "Blocca"} onClick={() => toggle(t)} data-testid={`ev-tag-toggle-${t.id}`}>
                      {t.blocked ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Ban className="h-4 w-4 text-amber-600" />}
                    </Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500" title="Rimuovi" onClick={() => del(t)} data-testid={`ev-tag-del-${t.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </>
                )}
              </div>
            ))}
      </div>
      <Button variant="outline" size="sm" disabled={busy} onClick={sync} data-testid="ev-tags-sync-btn"><Send className="mr-1 h-4 w-4" />Invia lista alla stazione</Button>
    </div>
  );
}

// ---------------- Gestione (anagrafica / località / potenza) ----------------
function ManageTab({ station, onSaved }) {
  const sid = station.id;
  const [f, setF] = useState({ name: "", owner_email: "", serial: "", model: "", require_auth: false, address: "", lat: "", lng: "", pod: "", max_current_a: "", admin_limit_w: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));

  useEffect(() => {
    (async () => {
      try {
        const d = (await api.get(`/ev/stations/${sid}`)).data;
        const p = (await api.get(`/ev/stations/${sid}/power`)).data || {};
        setF({
          name: d.nome || "", owner_email: d.owner_email || "", serial: d.serial || "", model: d.modello || "",
          require_auth: !!d.require_auth, address: d.indirizzo || "", lat: d.lat ?? "", lng: d.lng ?? "", pod: d.pod || "",
          max_current_a: p.max_current_a ?? "", admin_limit_w: p.admin_limit_w ?? "",
        });
      } catch { /* ignore */ }
    })();
  }, [sid]);

  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); onSaved && onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Salvataggio non riuscito"); }
    finally { setBusy(false); }
  };
  const num = (v) => (v === "" || v == null ? null : Number(v));

  return (
    <div className="space-y-3" data-testid="ev-manage-tab">
      <Section title="Anagrafica">
        <div className="grid grid-cols-2 gap-2">
          <div><Label className="text-xs">Nome</Label><Input value={f.name} onChange={set("name")} className="h-9" data-testid="ev-manage-name" /></div>
          <div><Label className="text-xs">Email cliente</Label><Input value={f.owner_email} onChange={set("owner_email")} className="h-9" data-testid="ev-manage-owner" /></div>
          <div><Label className="text-xs">Seriale</Label><Input value={f.serial} onChange={set("serial")} className="h-9" data-testid="ev-manage-serial" /></div>
          <div><Label className="text-xs">Modello</Label><Input value={f.model} onChange={set("model")} className="h-9" data-testid="ev-manage-model" /></div>
        </div>
        <label className="flex items-center gap-2 text-sm"><Switch checked={f.require_auth} onCheckedChange={set("require_auth")} data-testid="ev-manage-auth" />Richiede autenticazione (tessera)</label>
        <Button size="sm" disabled={busy} onClick={() => run(() => api.patch(`/ev/stations/${sid}`, { name: f.name, owner_email: f.owner_email || null, serial: f.serial || null, model: f.model || null, require_auth: f.require_auth }), "Anagrafica salvata")} data-testid="ev-manage-save-anagrafica">Salva anagrafica</Button>
      </Section>

      <Section title="Località">
        <Input placeholder="Indirizzo" value={f.address} onChange={set("address")} className="h-9" data-testid="ev-manage-address" />
        <div className="grid grid-cols-2 gap-2">
          <Input placeholder="Lat" value={f.lat} onChange={set("lat")} className="h-9" data-testid="ev-manage-lat" />
          <Input placeholder="Lng" value={f.lng} onChange={set("lng")} className="h-9" data-testid="ev-manage-lng" />
        </div>
        <Button size="sm" variant="outline" disabled={busy} onClick={() => run(() => api.put(`/ev/stations/${sid}/location`, { address: f.address || null, lat: num(f.lat), lng: num(f.lng) }), "Località salvata")} data-testid="ev-manage-save-location">Salva località</Button>
      </Section>

      <Section title="Potenza (tetto admin)">
        <div className="grid grid-cols-2 gap-2">
          <div><Label className="text-xs">Corrente max (A)</Label><Input placeholder="6–80" value={f.max_current_a} onChange={set("max_current_a")} className="h-9" data-testid="ev-manage-maxcur" /></div>
          <div><Label className="text-xs">Tetto admin (W)</Label><Input value={f.admin_limit_w} onChange={set("admin_limit_w")} className="h-9" data-testid="ev-manage-adminlimit" /></div>
        </div>
        <Button size="sm" variant="outline" disabled={busy} onClick={() => run(() => api.put(`/ev/stations/${sid}/power-config`, { max_current_a: num(f.max_current_a), admin_limit_w: num(f.admin_limit_w) }), "Potenza salvata")} data-testid="ev-manage-save-power">Salva potenza</Button>
      </Section>
    </div>
  );
}

export function StationAdminDialog({ station, onClose, onSaved }) {
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto" data-testid="ev-admin-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Settings2 className="h-5 w-5 text-primary" />Gestione · {station.nome || station.ocpp_id}</DialogTitle></DialogHeader>
        <Tabs defaultValue="manage">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="manage" data-testid="ev-tab-manage"><Settings2 className="mr-1 h-4 w-4" />Dati</TabsTrigger>
            <TabsTrigger value="maint" data-testid="ev-tab-maint"><Wrench className="mr-1 h-4 w-4" />Manutenzione</TabsTrigger>
            <TabsTrigger value="tags" data-testid="ev-tab-tags"><CreditCard className="mr-1 h-4 w-4" />Tessere</TabsTrigger>
          </TabsList>
          <TabsContent value="manage" className="mt-3"><ManageTab station={station} onSaved={onSaved} /></TabsContent>
          <TabsContent value="maint" className="mt-3"><MaintTab sid={station.id} /></TabsContent>
          <TabsContent value="tags" className="mt-3"><TagsTab sid={station.id} /></TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

export function RegisterStationDialog({ onClose, onDone }) {
  const [f, setF] = useState({ ocpp_id: "", name: "", owner_email: "", serial: "", model: "" });
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const submit = async () => {
    if (!f.ocpp_id.trim() || !f.name.trim()) { toast.error("Identificativo OCPP e nome obbligatori"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/ev/stations`, { ocpp_id: f.ocpp_id.trim(), name: f.name.trim(), owner_email: f.owner_email.trim() || undefined, serial: f.serial.trim() || undefined, model: f.model.trim() || undefined });
      setCreated(r.data || {});
      toast.success("Stazione registrata");
      onDone && onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Registrazione non riuscita"); }
    finally { setBusy(false); }
  };

  const authKey = created && (created.authorization_key || created.auth_key || created.key || created.authorizationKey);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="ev-register-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Plus className="h-5 w-5 text-primary" />Registra una stazione</DialogTitle></DialogHeader>
        {created ? (
          <div className="space-y-3" data-testid="ev-register-done">
            <p className="text-sm">Stazione «{created.name || f.name}» registrata.</p>
            {authKey && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm">
                <div className="mb-1 font-semibold text-amber-800">AuthorizationKey (mostrata una sola volta)</div>
                <code className="block break-all rounded bg-white px-2 py-1 font-mono text-xs" data-testid="ev-register-authkey">{authKey}</code>
                <p className="mt-1 text-xs text-amber-700">Copiala ora: serve per configurare la stazione fisica (LPTCP) e non sarà più visibile.</p>
              </div>
            )}
            <Button onClick={onClose} className="w-full" data-testid="ev-register-close">Chiudi</Button>
          </div>
        ) : (
          <div className="space-y-2">
            <div><Label className="text-xs">Identificativo OCPP *</Label><Input value={f.ocpp_id} onChange={set("ocpp_id")} className="h-9" data-testid="ev-register-ocpp" /></div>
            <div><Label className="text-xs">Nome *</Label><Input value={f.name} onChange={set("name")} className="h-9" data-testid="ev-register-name" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Email cliente</Label><Input value={f.owner_email} onChange={set("owner_email")} className="h-9" data-testid="ev-register-owner" /></div>
              <div><Label className="text-xs">Seriale</Label><Input value={f.serial} onChange={set("serial")} className="h-9" data-testid="ev-register-serial" /></div>
            </div>
            <div><Label className="text-xs">Modello</Label><Input value={f.model} onChange={set("model")} className="h-9" data-testid="ev-register-model" /></div>
            <Button onClick={submit} disabled={busy} className="w-full" data-testid="ev-register-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Registra"}</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
